import { faker } from '@faker-js/faker'

import { getDescriptionTemplate } from './DescriptionTemplate'
import { getPointRewards } from './PointRewards'
import { getPreviewQNA } from './PreviewQNA'
import { getPrice } from './Price'
import { getUserServiceOfferHospital } from './UserServiceOfferHospital'
import { getUserServiceOfferOption } from './UserServiceOfferOption'
import { getUserServiceOfferRating } from './UserServiceOfferRating'

import {
  Currency,
  RewardType,
  TargetGender,
  TargetLanguage,
  UserServiceOfferDetails,
  UserServiceOfferLogDetails,
} from '../../contracts/service-offer'
import { ExtraPriceChargeType, ExtraPriceType } from '../../contracts/service-offer/AdminServiceOfferExtraPrice'
import { OperationType } from '../../contracts/treatment'
import { asBuilder } from '../asBuilder'
import { ATFBadgeType } from '../../contracts/service-offer/ATFBadgeType'

export const getUserServiceOfferDetails = asBuilder((): UserServiceOfferDetails => {
  const { procedureRecommended, procedureProcess, medicalCases, sideEffects, downtime } = getDescriptionTemplate()

  const createRewardDescriptions = () =>
    faker.helpers.multiple(() => ({
      condition: faker.lorem.words(3),
      amount: faker.number.int().toString(),
    }))

  const createRewardDetail = () => ({
    title: faker.lorem.words(3),
    type: faker.helpers.arrayElement(Object.values(RewardType)),
    deepLink: faker.internet.url(),
    descriptions: createRewardDescriptions(),
  })

  return {
    id: faker.number.int(),
    title: faker.string.uuid(),
    description: faker.lorem.sentence(),

    recentConsultation: {
      id: faker.number.int(),
      createdAt: '2023-07-21T00:00:00+09:00',
    },

    period: {
      startsAt: '2021-07-21T00:00:00+09:00',
      endsAt: '2099-07-21T00:00:00+09:00',
    },
    available: faker.datatype.boolean(),
    operationType: faker.helpers.enumValue(OperationType),
    targetLanguage: faker.helpers.enumValue(TargetLanguage),
    targetGender: faker.helpers.enumValue(TargetGender),

    thumbnail: { url: faker.image.url() },
    cardImage: { url: faker.image.url() },
    descriptionImages: faker.helpers.multiple(() => ({ url: faker.image.url() })),

    price: getPrice(),
    pricePolicy: {
      exclusivePrice: faker.datatype.boolean(),
      includeVat: faker.datatype.boolean(),
    },
    extraPrices: faker.helpers.multiple(
      () => ({
        name: faker.lorem.words(3),
        chargeType: faker.helpers.enumValue(ExtraPriceChargeType),
        amount: faker.number.int({ min: 1000, max: 5000 }),
        currency: faker.helpers.enumValue(Currency),
        type: faker.helpers.enumValue(ExtraPriceType),
      }),
      {
        count: {
          min: 1,
          max: 3,
        },
      },
    ),
    options: faker.helpers.multiple(getUserServiceOfferOption),

    procedureRecommended,
    procedureProcess,
    medicalCases,
    sideEffects,
    downtime,

    hospital: getUserServiceOfferHospital(),
    rating: getUserServiceOfferRating(),

    qna: {
      totalQuestions: faker.number.int(),
      previews: faker.helpers.multiple(getPreviewQNA),
    },

    totalConsultationCount: faker.number.int(),
    pointRewards: getPointRewards(),
    addedInWishlist: faker.datatype.boolean(),
    surgicalRewardExist: faker.datatype.boolean(),
    crossBorder: faker.datatype.boolean(),
    rewards: {
      totalReward: {
        descriptions: faker.helpers.multiple(() => ({
          text: faker.lorem.sentence(),
          highlight: faker.datatype.boolean(),
        })),
        isVoucherType: faker.datatype.boolean(),
      },
      rewardDetails: faker.helpers.multiple(createRewardDetail),
    },

    reviewFirstWriteReward: {
      title: faker.lorem.words(3),
      description: {
        text: faker.lorem.sentence(),
        deepLink: faker.internet.url(),
      },
      rewardDetail: createRewardDetail(),
    },
    badges: faker.helpers.multiple(() => faker.helpers.enumValue(ATFBadgeType)),
  }
})

export const getUserServiceOfferLogDetails = asBuilder((): UserServiceOfferLogDetails => {
  return {
    event_id: faker.number.int(),
    event_title: faker.string.uuid(),
    cost: faker.number.int(),
    currency: faker.helpers.enumValue(Currency),
    hospital_id: faker.number.int(),
    hospital_name: faker.string.uuid(),
    hospital_country: faker.string.uuid(),
    hospital_region: faker.string.uuid(),
    operation_type: faker.string.uuid(),
    can_treatment_purchase: faker.datatype.boolean(),
    concern_body_part_tag_label_list: [faker.string.uuid()],
    concern_body_part_tag_name_list: [faker.string.uuid()],
    concern_tag_label_list: [faker.string.uuid()],
    concern_tag_name_list: [faker.string.uuid()],
    treatment_category_tag_id_list: [faker.number.int()],
    treatment_category_tag_label_list: [faker.string.uuid()],
    treatment_category_tag_name_list: [faker.string.uuid()],
    treatment_group_tag_label_list: [faker.string.uuid()],
    treatment_group_tag_name_list: [faker.string.uuid()],
    treatment_id_list: [faker.number.int()],
    treatment_label_list: [faker.string.uuid()],
    treatment_name_list: [faker.string.uuid()],
    assessment_state: faker.string.uuid(),
    can_reservation: faker.string.uuid(),
    target_language: faker.string.uuid(),
    has_description: faker.datatype.boolean(),
    has_procedure_recommended: faker.datatype.boolean(),
    has_procedure_process: faker.datatype.boolean(),
    has_downtime: faker.datatype.boolean(),
    has_medical_cases: faker.datatype.boolean(),
    has_side_effects: faker.datatype.boolean(),
    has_description_images: faker.datatype.boolean(),
    exclusive_price: faker.datatype.boolean(),
    home_page_reservation: faker.datatype.boolean(),
    options: Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => ({
      id: faker.string.uuid(),
      name: faker.string.uuid(),
      price: {
        discount_percentage: faker.number.int({ min: 0, max: 100 }),
        discounted_amount: faker.number.int({ min: 0, max: 1000 }),
        original_amount: faker.number.int({ min: 1000, max: 5000 }),
      },
      treatments: Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => ({
        id: faker.number.int(),
        name: faker.string.uuid(),
        price_properties: Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => ({
          id: faker.string.uuid(),
          name: faker.string.uuid(),
          values: Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => ({
            id: faker.string.uuid(),
            name: faker.string.uuid(),
          })),
        })),
        unit_property: {
          id: faker.string.uuid(),
          name: faker.string.uuid(),
          value: faker.number.int(),
        },
        times: faker.number.int({ min: 1, max: 10 }),
      })),
      price_properties: faker.string.uuid(),
      unit_properties: faker.string.uuid(),
      option_treatment_names: faker.string.uuid(),
      option_treatment_ids: faker.string.uuid(),
      discount_condition: faker.helpers.multiple(() => faker.lorem.word()),
    })),
    extra_prices: Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => ({
      type: faker.helpers.enumValue(ExtraPriceType),
      charge_type: faker.helpers.enumValue(ExtraPriceChargeType),
      amount: faker.number.int(),
    })),
    discount_condition: faker.helpers.multiple(() => faker.lorem.word()),
  }
})
